import { ExpandingTooltip } from "./ExpandingTooltip";

export {
  DocumentSelectorPanel as TTDocumentSelectorPanel,
  BatchInstanceDropdown as TTBatchInstanceDropdown,
  DeleteBatches as TTDeleteBatches,
  SaveTemplates as TTSaveTemplates,
  ReprocessBatches as TTReprocessBatches,
  SearchBatch as TTSearchBatch,
  SelectAll as TTSelectAllBatches,
  SelectBatch as TTSelectBatch,
  CollapseAll as TTCollapseAllBatches,
  BatchId as TTBatchId,
};

// Item 13
const DocumentSelectorPanel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Filter batches and select the documents you want to review or configure.
      </p>
    );
  return (
    <>
      <p>
        In the <strong>Document Selector</strong> panel, you can view documents
        in batches that have been processed by Transact or imported into
        Accelerator. Use the filters to further limit the documents displayed by
        classification type or potential extraction issues as described below.
      </p>
      <p>The Classification and Extraction filters are:</p>
      <ul>
        <li>
          <strong>Unknown</strong>
          <p>
            Documents classified as Unknown have not classified using a Specific
            or General template. Many of the documents will have been correctly
            classified as Unknown – for example any supplementary documents
            included in an ingested email that don’t have a Document Type
            configured in the system. These documents can be ignored.
          </p>
        </li>
        <li>
          <strong>General</strong>
          <p>
            Only displays documents classified using a General Classification
            template. If there is a Specific template for this document that
            hasn’t classified correctly, review the classification rules for
            that Specific template to determine if they should be adjusted to
            correctly classify this document (or if it didn’t classify due to
            OCR quality issues). If there is no Specific template for this
            document, consider configuring one.
          </p>
        </li>
        <li>
          <strong>General Format</strong>
          <p>
            Only displays documents classified using a General Format template.
            If the document has classified and extracted all data correctly
            there is no need to configure a Specific Template for this document.
          </p>
        </li>
        <li>
          <strong>Error</strong>
          <p>
            Only displays documents classified by a Specific template where the
            configured extraction rule has not fired for one or more of the
            document level fields, or the table Start Pattern has not fired.
            This is used to identify templates that may need updating.
          </p>
        </li>
        <li>
          <strong>Empty</strong>
          <p>
            Only displays documents that have extracted correctly but have a
            field that is allowed to fail (the Allow Empty flag was set to On)
            and provide an empty result that has done so (an extraction rule
            exists for a field but did not fire). The data may or not be on the
            document, so it is worth checking.
          </p>
        </li>
        <li>
          <strong>Multiple</strong>
          <p>
            Only displays documents that have classified as more than one
            template (with the same number of Start Patterns). The document may
            have classified against the correct template, but you may still want
            to review the Start Patterns and make them more specific. Selecting
            the document will display the list of other templates that the
            document has classified against at the top of the Classification
            panel (click the dropdown to see the template names).
          </p>
        </li>
        <li>
          <strong>Invalid</strong>
          <p>
            Only displays documents that have one or more fields/tables where
            the extracted data does not meet the Accelerator validation rules.
          </p>
        </li>
        <li>
          <strong>All</strong>
          <p>Displays all documents in the selected batch instances.</p>
        </li>
      </ul>
    </>
  );
};

// Item 15
const BatchInstanceDropdown: ExpandingTooltip = () => {
  return (
    <p>
      Select the batch instance (one or all) from which you want documents to be
      displayed.
    </p>
  );
};

// Item 16
const DeleteBatches: ExpandingTooltip = (short) => {
  if (short)
    return <p>Click to delete all selected batches from Accelerator.</p>;
  return (
    <>
      <p>
        When you have finished configuring documents, you should delete batches
        from Accelerator on a regular basis. This will prevent the system from
        running slowly.
      </p>
    </>
  );
};

// Item 17
const SaveTemplates: ExpandingTooltip = () => {
  // if (short) return (
  //     <p>Click to save any changes you have made to templates since the last Save.  </p>
  // )
  return (
    <>
      <p>
        Click to save any changes you have made to templates since the last
        Save.{" "}
      </p>
      {/* <p>
            Saving templates will also reprocess all templates of the same name as well as well as any documents that were previously classified as Unknown or General. This allows you to see the impact of your changes.
        </p> */}
    </>
  );
};

// Item 18
const ReprocessBatches: ExpandingTooltip = (short) => {
  if (short) return <p>Click to reprocess any batches that are selected.</p>;
  return (
    <>
      <p>
        For all selected batches, clicking Reprocess will rerun classification
        and extraction.
      </p>
    </>
  );
};

// Item 27
const SearchBatch: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>Enter search criteria to limit the batches that are displayed.</p>
    );
  return (
    <>
      <p>
        Once you have filtered the batches to be displayed using the Batch Class
        and Batch Instance filters, you can enter search terms to further
        restrict the batches that are displayed. The full set of documents will
        be displayed if the search found any documents that meet the search
        criteria. For example, if you search for a specific template name and
        only one template with that name is included in a batch, all documents
        in the batch will be displayed.
      </p>
      <p>Search terms are commonly used to filter by:</p>
      <ul>
        <li>Batch Instance ID (enter all or part of the batch instance ID) </li>
        <li>Document Type (e.g., Invoice) </li>
        <li>Template Name (search using all or part of the name). </li>
      </ul>
      <p>
        Click the <strong>x</strong> in the search box to clear a search.
      </p>
    </>
  );
};

// Item 28
const SelectAll: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Select to include all batches in any reprocess or batch delete actions.
      </p>
    );
  return (
    <>
      <p>
        Only batches that are selected will be reprocessed or deleted when those
        functions run. If no batches are selected, the system defaults to all
        batches being included in reprocess and batch delete actions.
      </p>
    </>
  );
};

// Item 29
const SelectBatch: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Select to include/exclude batches from any reprocess or batch delete
        actions.
      </p>
    );
  return (
    <>
      <p>
        Only batches that are selected will be reprocessed or deleted when those
        functions run.
      </p>
    </>
  );
};

// Item 30
const CollapseAll: ExpandingTooltip = () => {
  return <p>Expand/Collapse all batches to display/hide their documents.</p>;
};

// Item 31
const BatchId: ExpandingTooltip = () => {
  return (
    <p>
      The Batch ID generated by Transact, followed by the documents in that
      batch.
    </p>
  );
};
